import {
	Box,
	Checkbox,
	createStyles,
	Group,
	UnstyledButton,
} from '@mantine/core';
import type { GroupItemSettingSchema } from '@repo/api-codegen';
import type { EntityType } from '@repo/common/enums/entityType';
import { getEntityTypeDisplayInfo } from '@repo/common/utils/entityDisplayUtils.tsx';
import { Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { TeamRender } from '../../../components/TableV2/render';
import type { SettingsStore } from '../store';

import { VisibilityState } from '../constants';

interface SelectPanelTableRowProps {
	databuilderId: string;
	filteredItem: GroupItemSettingSchema;
	store: SettingsStore;
	level?: number;
}

interface SelectPanelTableRowStylesProp {
	level: number;
}

const useStyles = createStyles(
	(theme, { level }: SelectPanelTableRowStylesProp) => ({
		nameCol: {
			paddingLeft: `calc(${theme.spacing['2xl']} * ${level})`,
			width: '100%',
			flexWrap: 'nowrap',
		},
	})
);

export const SelectIntegrationGroupsPanelTableRow = observer(
	({
		databuilderId,
		filteredItem,
		store,
		level = 0,
	}: SelectPanelTableRowProps) => {
		const { classes, theme } = useStyles({ level });

		const liveItem = store.settingsMap[databuilderId];
		const hasChildren = filteredItem.children?.length > 0;

		const EntityIcon = useMemo(
			() =>
				getEntityTypeDisplayInfo(liveItem.entity_type as EntityType).renderIcon(
					theme.other.iconSize.sm
				),
			[liveItem.entity_type, theme.other.iconSize.sm]
		);

		const toggleVisibility = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				store.setVisible(liveItem.databuilder_id, event.target.checked);
			},
			[liveItem.databuilder_id, store]
		);

		const toggleExpanded = useCallback(() => {
			store.setExpanded(
				liveItem.databuilder_id,
				!store.isExpanded(liveItem.databuilder_id)
			);
		}, [liveItem.databuilder_id, store]);

		const toggleMapping = useCallback(
			(id: string) => (value: unknown) => {
				store.setCustomMappingTeams(id, value as string[]);
			},
			[store]
		);

		return (
			<>
				<Group role="row">
					<Box role="cell">
						<Group className={classes.nameCol}>
							<Checkbox
								checked={[
									VisibilityState.VISIBLE,
									VisibilityState.INDETERMINATE,
								].includes(store.isVisible(liveItem.databuilder_id))}
								indeterminate={
									store.isVisible(liveItem.databuilder_id) ===
									VisibilityState.INDETERMINATE
								}
								onChange={toggleVisibility}
							/>
							<UnstyledButton onClick={toggleExpanded}>
								<Group spacing="sm" noWrap>
									{EntityIcon}
									<Text maw={240} truncate size="xs">
										{liveItem.title}
									</Text>
									{hasChildren && (
										<Icon
											name={
												store.isExpanded(liveItem.databuilder_id)
													? 'chevronDown'
													: 'chevronRight'
											}
										/>
									)}
								</Group>
							</UnstyledButton>
						</Group>
					</Box>
					<Box role="cell">
						<TeamRender
							record={{
								id: liveItem.databuilder_id,
								teams: liveItem.mapping_teams,
							}}
							onChange={toggleMapping}
							placeholder={'Inherited'}
						/>
					</Box>
				</Group>
				{hasChildren &&
					store.isExpanded(liveItem.databuilder_id) &&
					filteredItem.children.map((child) => (
						<SelectIntegrationGroupsPanelTableRow
							key={child.databuilder_id}
							databuilderId={child.databuilder_id}
							store={store}
							level={level + 1}
							filteredItem={child}
						/>
					))}
			</>
		);
	}
);
